// UFO — a classic flying saucer parked on three stubby legs, straight out of a
// friendly Saturday-morning cartoon: a plump teal dish with a silver belly, a
// glass dome with a tiny green pilot blob peeking out, and a ring of coloured
// lights around the rim. Toy-like: fat silhouette, oversized studs, warm flat
// colours, everything readable at a glance.
//
// Frame: y = 0 is the landing pad (the leg feet touch it), +Z is where the
// pilot looks. About 1.5 m tall over a 2x2 tile pad.
//
// Animation layout (flat nodes, deliberate pivots):
//   - "saucer"     : the whole dish + dome + pilot (not the legs). Hovers with
//                    a soft bob over the parked legs. Pivot at the dish base.
//   - "light-ring" : every light stud around the rim shares this name, so the
//                    whole ring spins together about the vertical axis. It also
//                    bobs along with the saucer in idle so the ring never
//                    detaches from the rim.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const TEAL = "#3aa8a0";       // main dish — friendly toy teal
const TEAL_DARK = "#2c827b";  // shaded underside / rim band
const SILVER = "#c3cad1";     // belly plate, legs, antenna
const SILVER_DARK = "#98a1a9"; // shaded silver (leg shading, dome collar)
const CREAM = "#f6efe3";      // trim: dome collar ring, foot pads
const GLASS = "#bfe7f0";      // the cockpit dome
const PILOT = "#9fd468";      // the little pilot blob — happy alien green
const EYE = "#35402e";        // pilot eyes (warm dark, never pure black)
const LIGHT_A = "#e8604c";    // rim lights, coral
const LIGHT_B = "#f7c948";    // rim lights, amber
const LIGHT_C = "#f2f0e4";    // rim lights, warm white

// Frame heights.
const RIM_Y = 0.62;                        // centre of the rim band + light ring
const SAUCER_PIVOT: [number, number, number] = [0, 0.4, 0];
const RING_PIVOT: [number, number, number] = [0, RIM_Y, 0];

// One stubby landing leg: a silver strut leaning out from the dish underside
// down to a cream foot pad. Placed by azimuth so the +Z pilot view stays clear.
// Static — the saucer hovers above these.
function leg(azimuth: number): Part[] {
  const x = Math.sin(azimuth), z = Math.cos(azimuth);
  return [
    // The strut: leans outward from under the dish to the foot. eulerOrder YXZ
    // = yaw to the azimuth first, then lean radially.
    {
      geometry: new THREE.CylinderGeometry(0.05, 0.06, 0.46, 8),
      color: SILVER_DARK,
      position: [x * 0.5, 0.24, z * 0.5],
      rotation: [0.42, azimuth, 0],
      eulerOrder: "YXZ",
    },
    // Cream foot pad — a squashed dome that grounds the leg.
    {
      geometry: new THREE.SphereGeometry(0.12, 10, 6),
      color: CREAM,
      position: [x * 0.6, 0.035, z * 0.6],
      scale: [1, 0.45, 1],
    },
  ];
}

function ufoParts(): Part[] {
  const parts: Part[] = [
    // Legs at 60 / 180 / 300 degrees, leaving the +Z pilot view open.
    ...leg(Math.PI / 3),
    ...leg(Math.PI),
    ...leg((5 * Math.PI) / 3),

    // Silver belly plate: the flat underside the legs meet.
    { geometry: new THREE.CylinderGeometry(0.34, 0.26, 0.10, 14), color: SILVER,
      position: [0, 0.42, 0], name: "saucer" },
    // Lower dish: flares out from the belly to the rim.
    { geometry: new THREE.CylinderGeometry(0.86, 0.32, 0.20, 16), color: TEAL_DARK,
      position: [0, 0.55, 0], name: "saucer" },
    // Rim band: the fat equator the lights ride on.
    { geometry: new THREE.CylinderGeometry(0.90, 0.90, 0.10, 16), color: SILVER,
      position: [0, RIM_Y, 0], name: "saucer" },
    // Upper dish: tapers back in toward the dome.
    { geometry: new THREE.CylinderGeometry(0.36, 0.86, 0.20, 16), color: TEAL,
      position: [0, 0.75, 0], name: "saucer" },

    // Dome collar: a cream ring seating the glass.
    { geometry: new THREE.CylinderGeometry(0.40, 0.40, 0.06, 14), color: CREAM,
      position: [0, 0.87, 0], name: "saucer" },
    // The glass dome (a squashed sphere; its lower half hides in the dish).
    { geometry: new THREE.SphereGeometry(0.36, 14, 10), color: GLASS,
      position: [0, 0.90, 0], scale: [1, 0.82, 1], name: "saucer" },

    // The tiny pilot blob, head poking out through a hatch ring at the dome
    // crown so it stays visible (the renderer paints glass opaque).
    { geometry: new THREE.CylinderGeometry(0.17, 0.17, 0.045, 12), color: CREAM,
      position: [0, 1.14, 0], name: "saucer" },
    { geometry: new THREE.SphereGeometry(0.15, 10, 8), color: PILOT,
      position: [0, 1.17, 0], scale: [1, 1.15, 1], name: "saucer" },
    { geometry: new THREE.SphereGeometry(0.03, 6, 5), color: EYE,
      position: [-0.058, 1.22, 0.13], name: "saucer" },
    { geometry: new THREE.SphereGeometry(0.03, 6, 5), color: EYE,
      position: [0.058, 1.22, 0.13], name: "saucer" },

    // The pilot's antenna + amber ball, sprouting from the head.
    { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.16, 6), color: SILVER,
      position: [0, 1.4, 0], name: "saucer" },
    { geometry: new THREE.SphereGeometry(0.045, 8, 6), color: LIGHT_B,
      position: [0, 1.49, 0], name: "saucer" },
  ];

  // The ring of oversized coloured light studs around the rim. Every stud is
  // named "light-ring" so the whole ring spins as one node.
  const LIGHTS = 9;
  const COLOURS = [LIGHT_A, LIGHT_B, LIGHT_C];
  for (let i = 0; i < LIGHTS; i++) {
    const a = (i / LIGHTS) * Math.PI * 2;
    parts.push({
      geometry: new THREE.SphereGeometry(0.065, 8, 6),
      color: COLOURS[i % COLOURS.length],
      position: [Math.sin(a) * 0.90, RIM_Y, Math.cos(a) * 0.90],
      name: "light-ring",
    });
  }
  return parts;
}

export const ufo = defineModel({
  id: "ufo",
  batchId: "mess",
  name: "UFO",
  tags: ["mess", "cute", "animated", "vehicle", "ufo", "saucer", "space", "alien", "toy"],
  summary: "A parked teal flying saucer with a glass dome, a pilot blob and a spinning light ring.",
  description:
    "A classic cartoon flying saucer parked on three stubby silver legs: a plump " +
    "teal dish with a silver belly and rim, a glass dome with a tiny friendly " +
    "green pilot blob looking out, and a ring of coral, amber and warm-white " +
    "light studs around the rim. The saucer body hovers with a soft bob over its " +
    "legs in idle, and the light ring spins about the vertical axis.",
  author: "models",
  footprint: 1.0,
  tiles: { w: 2, h: 2 },
  parts: ufoParts,
  animations: [
    // Idle — the parked hover: the whole dish (and the light ring with it, so
    // the studs never leave the rim) breathes up and down over the legs.
    {
      id: "idle",
      label: "Hover bob",
      duration: 3.0,
      loop: true,
      tracks: [
        {
          target: "saucer", pivot: SAUCER_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.035, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "light-ring", pivot: RING_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.035, 0], ease: "sine" },
            { t: 3.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Light spin — the signature move: the whole ring of studs turns about the
    // vertical axis, a steady carousel of colour around the rim.
    {
      id: "light-spin",
      label: "Light ring spin",
      duration: 3.6,
      loop: true,
      tracks: [
        {
          target: "light-ring", pivot: RING_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 1.2, value: [0, (Math.PI * 2) / 3, 0] },
            { t: 2.4, value: [0, (Math.PI * 4) / 3, 0] },
            { t: 3.6, value: [0, Math.PI * 2, 0] },
          ],
        },
      ],
    },
  ],
});
